import { SuccessfullAddArticleFolderDialog } from '../components/articleFolder/successfullAddArticleFolderDialog';
import { AddArticleFolderDialog } from '../components/articleFolder/addArticleFolderDialog';
import ArticleFolderPageHeader from '../components/articleFolder/articleFolderPageHeader';
import ArticleFolderFiltration from '../components/articleFolder/articleFolderFiltration';
import ArticleFolderList from '../components/articleFolder/articleFolderList';
import ArticleFolderGrid from '../components/articleFolder/articleFolderGrid';
import { useState, useCallback, useEffect } from 'react';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { useHasPermission } from '../hooks/useHasPermission';
import { Divider, Box, LinearProgress } from '@mui/material';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';
import '../css/roles/roles.css';

const ArticleFolders = () => {

    const hasArticleFolderCreatePermission = useHasPermission('dossier_create');
    const hasArticleFolderUpdatePermission = useHasPermission('dossier_update');

    const [openAddArticleFolder, setOpenAddArticleFolder] = useState(false);
    const [openSuccessfulAddArticleFolder, setOpenSuccessfulAddArticleFolder] = useState(false);

    const [loading, setLoading] = useState(true);
    const [articleFolders, setArticleFolders] = useState([])
    const [totalArticleFolders, setTotalArticleFolders] = useState(0);
    const [filteredArticleFoldersCount, setFilteredArticleFoldersCount] = useState('');
    const [articleFoldersFetched, setArticleFoldersFetched] = useState(0);
    const [selectedDisplayMode, setSelectedDisplayMode] = useState(JSON.parse(localStorage.getItem('articleFolderDisplayMode')) || 'grid');
    const defaultSortBy = {
        title: null,
        description: null,
    };

    const [listVersion, setListVersion] = useState(0);
    const [sortBy, setSortBy] = useState(() => {
        const storedSortBy = JSON.parse(localStorage.getItem('articleFolderSortBy')) || {};
        return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
    });
    const [articleFoldersFilters, setArticleFoldersFilters] = useState(
        JSON.parse(localStorage.getItem('articleFoldersFilters')) ||
        { debouncedSearch: '' }
    );

    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'article-folders',
        pathname: '/dashboard/dossiers_articles',
        displayMode: selectedDisplayMode,
        itemSizes: { grid: 280, list: 70 },
        resetOnFilters: articleFoldersFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(articleFoldersFetched);

    useEffect(() => {
        setLoading(true)
    }, [articleFoldersFilters]);

    useEffect(() => {
        localStorage.setItem('articleFoldersFilters', JSON.stringify(articleFoldersFilters));
    }, [articleFoldersFilters]);

    useEffect(() => {
        localStorage.setItem('articleFolderSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem('articleFolderDisplayMode', JSON.stringify(selectedDisplayMode));
    }, [selectedDisplayMode]);

    const fetchArticleFolders = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('article-folder/list', { params: { offset: adjustedOffset, limit, filters: articleFoldersFilters, sortBy, reload: listVersion } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedArticleFolders = [];
                // Update the fetched records in their correct positions
                response?.data?.articleFolders?.forEach((articleFolder, index) => { updatedArticleFolders[adjustedOffset + index] = articleFolder });
                // Update the state with the new articleFolder array
                setArticleFolders(updatedArticleFolders);
                setFilteredArticleFoldersCount(response?.data?.filteredArticleFoldersCount);
                setTotalArticleFolders(response?.data?.totalArticleFolders);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching articleFolders:', error);
            CustomErrorLogger(error, 'fetch articleFolders in articleFolders page');
        }
    }, [articleFoldersFilters, offset, sortBy, listVersion]);

    const moveArticleFolder = async (id, direction) => {
        try {
            const response = await axios.patch(`article-folder/${id}/move`, { direction });
            if (response.data?.moved) {
                setSortBy({ title: null, description: null });
                setListVersion((value) => value + 1);
            }
        } catch (error) {
            CustomErrorLogger(error, 'move article folder');
        }
    };

    useEffect(() => {
        fetchArticleFolders()
    }, [fetchArticleFolders]);


    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="dossiers d'articles" list={true}>
            <ArticleFolderPageHeader
                setOpenAddArticleFolder={setOpenAddArticleFolder}
                hasArticleFolderCreatePermission={hasArticleFolderCreatePermission}
            />
            <Divider />

            {loading ?
                <Box>
                    <ArticleFolderFiltration
                        filtersEmpty={filtersEmpty}
                        articleFoldersFilters={articleFoldersFilters}
                        setArticleFoldersFilters={setArticleFoldersFilters}
                        totalArticleFolders={totalArticleFolders}
                        articleFoldersFetched={articleFoldersFetched}
                        filteredArticleFoldersCount={filteredArticleFoldersCount}
                        selectedDisplayMode={selectedDisplayMode}
                        setSelectedDisplayMode={setSelectedDisplayMode}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                totalArticleFolders === 0 ?
                    <NoRecord
                        description="Aucun dossier d'articles ajouté pour le moment"
                        buttonTitle="Ajouter un dossier"
                        handleOpenDialog={() => setOpenAddArticleFolder(true)}
                        hasPermission={hasArticleFolderCreatePermission}
                    /> :
                    <Box>
                        <ArticleFolderFiltration
                            filtersEmpty={filtersEmpty}
                            articleFoldersFilters={articleFoldersFilters}
                            setArticleFoldersFilters={setArticleFoldersFilters}
                            totalArticleFolders={totalArticleFolders}
                            articleFoldersFetched={articleFoldersFetched}
                            filteredArticleFoldersCount={filteredArticleFoldersCount}
                            selectedDisplayMode={selectedDisplayMode}
                            setSelectedDisplayMode={setSelectedDisplayMode}
                        />

                        {selectedDisplayMode === 'grid' ? (
                            <ArticleFolderGrid
                                scrollRef={scrollRef}
                                articleFolders={articleFolders}
                                setOffset={setOffset}
                                filteredArticleFoldersCount={filteredArticleFoldersCount}
                                setArticleFoldersFetched={setArticleFoldersFetched}
                                canReorder={hasArticleFolderUpdatePermission}
                                onMove={moveArticleFolder}
                            />
                        ) : (
                            <ArticleFolderList
                                scrollRef={scrollRef}
                                articleFolders={articleFolders}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                setOffset={setOffset}
                                filteredArticleFoldersCount={filteredArticleFoldersCount}
                                setArticleFoldersFetched={setArticleFoldersFetched}
                                canReorder={hasArticleFolderUpdatePermission}
                                onMove={moveArticleFolder}
                            />
                        )}

                    </Box>
            }

            {openAddArticleFolder &&
                <AddArticleFolderDialog
                    setArticleFolders={setArticleFolders}
                    setTotalArticleFolders={setTotalArticleFolders}
                    setFilteredArticleFoldersCount={setFilteredArticleFoldersCount}
                    openAddArticleFolder={openAddArticleFolder}
                    setOpenAddArticleFolder={setOpenAddArticleFolder}
                    setOpenSuccessfulAddArticleFolder={setOpenSuccessfulAddArticleFolder}
                />
            }

            {openSuccessfulAddArticleFolder &&
                <SuccessfullAddArticleFolderDialog
                    openSuccessfulAddArticleFolder={openSuccessfulAddArticleFolder}
                    setOpenSuccessfulAddArticleFolder={setOpenSuccessfulAddArticleFolder}
                    setOpenAddArticleFolder={setOpenAddArticleFolder}
                />
            }

        </Page>
        </ListNavigationProvider>
    );
};

export default ArticleFolders;